import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import { useTheme } from '../../../../theme/ThemeProvider';
import { CompanyImage, CompanyImageType } from '../../../../types/models';
import { CompanyImageService } from '../../../../services/CompanyImageService';
import { DraggableLogo } from '../../../Dashboard/Shared/CompanyImages/DraggableLogo';

interface CompanyImagesSidebarProps {
  onOpenImageManager?: () => void;
}

export const CompanyImagesSidebar: React.FC<CompanyImagesSidebarProps> = ({
  onOpenImageManager
}) => {
  const theme = useTheme();
  const [images, setImages] = useState<CompanyImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<CompanyImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<CompanyImageType | 'all'>('all');

  // Load images on component mount
  useEffect(() => {
    loadImages();
  }, []);

  // Filter images when type filter changes
  useEffect(() => {
    filterImages();
  }, [images, typeFilter]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedImages = await CompanyImageService.getCompanyImages();
      setImages(fetchedImages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load company images';
      console.error('Error loading company images:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filterImages = () => {
    let filtered = images;

    if (typeFilter !== 'all') {
      filtered = filtered.filter(image => image.imageType === typeFilter);
    }

    setFilteredImages(filtered);
  };

  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setTypeFilter(event.target.value as CompanyImageType | 'all');
  };

  const handleRefresh = () => {
    loadImages();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
        <CircularProgress size={40} sx={{ color: theme.colors.green }} />
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 2,
            color: 'rgba(27, 46, 60, 0.7)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}
        >
          Loading images...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {error.includes('member of a company') && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To use company assets, please join or create a company from the Shared tab.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#FFFFFF' }}>
      {/* Filter Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(27, 46, 60, 0.2)' }}>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: theme.colors.green }}>Filter by Type</InputLabel>
          <Select
            value={typeFilter}
            label="Filter by Type"
            onChange={handleTypeFilterChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.green
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.green
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.green
              }
            }}
          >
            <MenuItem value="all">All Types</MenuItem>
            {Object.values(CompanyImageType).map((type) => (
              <MenuItem key={type} value={type}>
                <Typography 
                  sx={{ 
                    textTransform: 'capitalize',
                    color: theme.colors.green,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  {type}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Images Grid */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {filteredImages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography 
              variant="body2" 
              sx={{
                color: theme.colors.green,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
            >
              {typeFilter === 'all' 
                ? 'No images found' 
                : `No ${typeFilter} images found`
              }
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {filteredImages.map((image) => (
              <DraggableLogo
                key={image.id}
                image={image}
              />
            ))}
          </Box>
        )}
      </Box>

    </Box>
  );
};
